<template>
  <modal
    :show="isVisible"
    :title="t('Detalhes do lead')"
    elem-id="modal_site_leads"
    size="large"
    :close-label="t('Fechar')"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Read-only detail. There is no Form layer in this domain and no save
         button on this modal: the record is the visitor's own submission and
         the panel never writes. -->
    <div v-if="isVisible" class="site-leads-detail" data-testid="site-leads-detail">
      <div class="site-leads-detail-grid">
        <div class="site-leads-detail-item" data-testid="site-leads-detail-formulario">
          <span class="site-leads-detail-label">{{ t('Formulário') }}</span>
          <span class="site-leads-detail-value">{{ textoOuTraco(envelope?.formulario ?? lead?.formulario) }}</span>
        </div>
        <div class="site-leads-detail-item" data-testid="site-leads-detail-versao">
          <span class="site-leads-detail-label">{{ t('Versão do formulário') }}</span>
          <span class="site-leads-detail-value">{{ versaoTexto }}</span>
        </div>
        <div class="site-leads-detail-item" data-testid="site-leads-detail-occurred-at">
          <span class="site-leads-detail-label">{{ t('Enviado pelo visitante em') }}</span>
          <span class="site-leads-detail-value">{{ dataTexto(envelope?.occurred_at) }}</span>
        </div>
        <div class="site-leads-detail-item" data-testid="site-leads-detail-recebido-em">
          <span class="site-leads-detail-label">{{ t('Recebido em') }}</span>
          <span class="site-leads-detail-value">{{ dataTexto(lead?.recebido_em) }}</span>
        </div>
        <div class="site-leads-detail-item" data-testid="site-leads-detail-event-id">
          <span class="site-leads-detail-label">{{ t('Identificador do evento') }}</span>
          <span class="site-leads-detail-value">{{ textoOuTraco(envelope?.event_id ?? lead?.event_id) }}</span>
        </div>
        <div class="site-leads-detail-item" data-testid="site-leads-detail-tenant">
          <span class="site-leads-detail-label">{{ t('Marca') }}</span>
          <span class="site-leads-detail-value">{{ textoOuTraco(envelope?.tenant ?? lead?.tenant) }}</span>
        </div>
      </div>

      <!-- The answers, rendered GENERICALLY: the field list belongs to the
           site's tenant pack, so the panel labels each key by its own name and
           never assumes which fields a form collects. -->
      <div class="site-leads-detail-block" data-testid="site-leads-detail-dados">
        <span class="site-leads-detail-label">{{ t('Respostas') }}</span>
        <p v-if="dadosEntradas.length === 0" class="site-leads-detail-message">{{ t('Sem respostas registradas') }}</p>
        <dl v-else class="site-leads-kv">
          <template v-for="entrada in dadosEntradas" :key="entrada.chave">
            <dt :data-testid="`site-leads-dado-chave-${entrada.chave}`">{{ entrada.chave }}</dt>
            <dd :data-testid="`site-leads-dado-${entrada.chave}`">
              <pre v-if="entrada.bloco" class="site-leads-json">{{ entrada.valor }}</pre>
              <span v-else>{{ entrada.valor }}</span>
            </dd>
          </template>
        </dl>
      </div>

      <div class="site-leads-detail-block" data-testid="site-leads-detail-campanha">
        <span class="site-leads-detail-label">{{ t('Campanha') }}</span>
        <p v-if="campanhaEntradas.length === 0" class="site-leads-detail-message">{{ t('Sem dados de campanha') }}</p>
        <dl v-else class="site-leads-kv">
          <template v-for="entrada in campanhaEntradas" :key="entrada.chave">
            <dt :data-testid="`site-leads-campanha-chave-${entrada.chave}`">{{ entrada.chave }}</dt>
            <dd :data-testid="`site-leads-campanha-${entrada.chave}`">
              <pre v-if="entrada.bloco" class="site-leads-json">{{ entrada.valor }}</pre>
              <span v-else>{{ entrada.valor }}</span>
            </dd>
          </template>
        </dl>
      </div>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSiteLeads } from '../../composables/useSiteLeads';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log, error as logError } from '../../utils/DebugLogger';
import type { SiteLeads_LeadRow } from '../../contracts/SiteLeads_LeadRow';

type LeadId = number | string;

interface ApiError extends Error {
  response?: { data?: { error?: string } };
}

interface ModalOpenPayload {
  id?: LeadId;
  action?: 'detail';
}

interface Entrada {
  chave: string;
  valor: string;
  bloco: boolean;
}

const { t } = useI18n();
const { fetchLead } = useSiteLeads();
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter();

const isVisible = ref<boolean>(false);
const lead = ref<SiteLeads_LeadRow | null>(null);

const envelope = computed(() => lead.value?.payload ?? null);

const versaoTexto = computed<string>(() => {
  const versao = envelope.value?.formulario_versao;
  return versao === undefined || versao === null ? '-' : String(versao);
});

/**
 * A key/value view of a free-form object. A scalar prints as text; a nested
 * object or list prints as pretty JSON, because the panel has no schema for it
 * and inventing a layout would misrepresent what the visitor actually sent.
 */
const paraEntradas = (objeto?: Record<string, unknown> | null): Entrada[] => {
  if (!objeto || typeof objeto !== 'object') return [];
  return Object.entries(objeto)
    .filter(([, valor]) => valor !== null && valor !== undefined && valor !== '')
    .map(([chave, valor]) => {
      const composto = typeof valor === 'object';
      return {
        chave,
        valor: composto ? JSON.stringify(valor, null, 2) : String(valor),
        bloco: composto
      };
    });
};

const dadosEntradas = computed<Entrada[]>(() => paraEntradas(envelope.value?.data));
const campanhaEntradas = computed<Entrada[]>(() => paraEntradas(envelope.value?.campanha));

const textoOuTraco = (valor?: string | null): string => {
  const texto = (valor ?? '').trim();
  return texto === '' ? '-' : texto;
};

const dataTexto = (iso?: string | null): string => {
  if (!iso) return '-';
  return formatDateTime(fromApiDateTimeFormat(iso)) || '-';
};

const openForDetail = async (leadId: LeadId): Promise<void> => {
  log('SiteLeadsModal', 'openForDetail', 'Opening modal for lead', leadId);
  isVisible.value = true;
  lead.value = null;

  const id = typeof leadId === 'string' ? parseInt(leadId, 10) : leadId;

  try {
    const carregado = await fetchLead(id);
    if (!carregado) {
      throw new Error('Dados do lead não encontrados');
    }
    lead.value = carregado;
  } catch (err) {
    const error = err as ApiError;
    logError('SiteLeadsModal', 'openForDetail', 'Error loading lead', { leadId, error: error.message });
    const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
    eventBus.emit('globalMessage.error', t('Erro ao carregar lead') + ': ' + errorMessage);
  }
};

const handleClose = (): void => {
  log('SiteLeadsModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  lead.value = null;
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('SiteLeadsModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data?.id !== undefined && data?.id !== null) {
    void openForDetail(data.id);
  }
};

onMounted(() => {
  log('SiteLeadsModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.site-leads.open', handleModalOpen);
  eventBus.on('modal.site-leads.close', handleClose);
});

onBeforeUnmount(() => {
  log('SiteLeadsModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.site-leads.open', handleModalOpen);
  eventBus.off('modal.site-leads.close', handleClose);
});

defineExpose({
  openForDetail
});
</script>

<style scoped>
.site-leads-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.site-leads-detail-item,
.site-leads-detail-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.site-leads-detail-block {
  margin-top: 16px;
}

.site-leads-detail-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--p-text-muted-color);
}

.site-leads-detail-value {
  font-size: 14px;
  word-break: break-word;
}

.site-leads-detail-message {
  margin: 0;
  font-size: 14px;
}

.site-leads-kv {
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: 4px 16px;
  margin: 4px 0 0 0;
  font-size: 14px;
}

.site-leads-kv dt {
  font-weight: 600;
  word-break: break-word;
}

.site-leads-kv dd {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.site-leads-json {
  margin: 0;
  padding: 8px;
  border-radius: 4px;
  background: var(--p-surface-100);
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
